import type AliRtcEngine from 'aliyun-rtc-sdk';
import type { LiveDebugLogger, LiveDebugValue } from '@/app/[locale]/(app)/vidu-stream/live-debug-log';
import { describeError } from '@/app/[locale]/(app)/vidu-stream/live-debug-log';
import { isRtcCameraGumStream } from '../../volc-rtc/camera-lifecycle';
import {
  hasMirroredCameraPlayback,
  pauseMirroredCameraPlayback,
  resumeMirroredCameraPlayback,
  stopMirroredCameraSource,
} from '../../volc-rtc/mirror-camera-stream';
import { getRtcSupportStatus } from '../../volc-rtc/rtc-support';
import { openRtcCameraStream, warmupRtcMicrophoneIfNeeded } from '../../volc-rtc/warmup-rtc-microphone';
import type { RemoteVideoBinding } from './live-call';
import { normalizeError, pickDisplayBinding } from './live-call';
import { teardownRtcEngine, waitForRtcEngineTeardown } from './rtc-engine-teardown';

type RtcSdkModule = typeof import('aliyun-rtc-sdk');

type LoggedRtcConnectionStatus = 'connected' | 'reconnecting' | 'failed';

export type AutoPlayBlockedKind = 'audio' | 'video';

const LIVE_SCREEN_SHARE_LOG_PREFIX = '[live-screen-share]';

const infoScreenShare = (stage: string, data?: Record<string, unknown>): void => {
  console.info(LIVE_SCREEN_SHARE_LOG_PREFIX, stage, data ?? {});
};

const warnScreenShare = (stage: string, data?: Record<string, unknown>): void => {
  console.warn(LIVE_SCREEN_SHARE_LOG_PREFIX, stage, data ?? {});
};

// 采集到的屏幕 track 实际参数：对端看不到画面时用来判断是采集侧（track 已 ended / 分辨率异常）
// 还是推流/后端侧的问题。getSettings 在部分环境可能不存在，故做可选调用。
const describeScreenShareTrack = (track: MediaStreamTrack): Record<string, unknown> => {
  const settings = typeof track.getSettings === 'function' ? track.getSettings() : undefined;
  return {
    track_id: track.id,
    track_label: track.label,
    ready_state: track.readyState,
    enabled: track.enabled,
    muted: track.muted,
    width: settings?.width,
    height: settings?.height,
    frame_rate: settings?.frameRate,
  };
};

const errorScreenShare = (stage: string, err: unknown, data?: Record<string, unknown>): void => {
  const details = {
    error_name: err instanceof Error ? err.name : typeof err,
    error_code: err instanceof Error ? (err as Error & { code?: unknown }).code : undefined,
    error_message: err instanceof Error ? err.message : String(err),
    ...(data ?? {}),
  };
  console.error(LIVE_SCREEN_SHARE_LOG_PREFIX, stage, details, err);
};

// 诊断：多次开关后「本端有 stats、对端收不到」的根因排查。
// 读 SDK publisher 内部真实发布状态，和我们采集到的 track 对比：
// - published_screen_track_id 与我们 attach 的 track id 不一致 → stop/start 后新 track 没接上发布流
// - has_screen_track_in_media_stream=false → screen track 不在实际发布的 MediaStream 里（本端 outbound
//   stats 可能来自占位/旧轨），对端自然订阅不到画面
// publisher / 这些 getter 是 SDK 内部结构，做全程可选访问，取不到不影响主流程。
const describeScreenPublishState = (engine: AliRtcEngine, expectedTrackId?: string): Record<string, unknown> => {
  try {
    const publisher = (engine as unknown as { publisher?: Record<string, unknown> }).publisher;
    if (!publisher) {
      return { publisher_available: false };
    }
    const publishedScreenTrack = publisher.screenTrack as MediaStreamTrack | undefined;
    return {
      publisher_available: true,
      published_screen_track_id: publishedScreenTrack?.id,
      published_screen_track_ready_state: publishedScreenTrack?.readyState,
      expected_track_id: expectedTrackId,
      track_id_matches: expectedTrackId ? publishedScreenTrack?.id === expectedTrackId : undefined,
      has_screen_track_in_media_stream: publisher.hasScreenTrackInMS,
      has_screen_share: publisher.hasScreenShare,
      publish_local_screen_enabled: publisher.publishLocalScreenStreamEnabled,
      screen_pub_state: publisher.screenPubState,
    };
  } catch (err) {
    return { publisher_available: false, read_error: err instanceof Error ? err.message : String(err) };
  }
};

// SDK 内部对象图里定位真正的 RTCPeerConnection：按 .d.ts 猜路径已被误导过一次（publishStreams.screen.pc
// 取不到），改成运行时广度优先扫描 engine，用 instanceof RTCPeerConnection 判定，避开路径/命名假设。
// 收集所有 pc，优先返回 signalingState=stable 且 localDescription 里含 m=video 的那个（屏幕辅流所在）。
const findPeerConnections = (engine: AliRtcEngine): RTCPeerConnection[] => {
  if (typeof RTCPeerConnection === 'undefined') {
    return [];
  }
  const found: RTCPeerConnection[] = [];
  const seen = new Set<unknown>();
  const queue: unknown[] = [engine];
  let visited = 0;
  // 限制访问节点数，防止对象图有环或过大导致卡顿；SDK 结构不深，几百个节点足够覆盖。
  while (queue.length > 0 && visited < 2_000) {
    const node = queue.shift();
    visited += 1;
    if (node === null || typeof node !== 'object' || seen.has(node)) {
      continue;
    }
    seen.add(node);
    if (node instanceof RTCPeerConnection) {
      if (!found.includes(node)) {
        found.push(node);
      }
      continue;
    }
    for (const key of Object.keys(node as Record<string, unknown>)) {
      try {
        queue.push((node as Record<string, unknown>)[key]);
      } catch {
        // 个别 getter 访问即抛错（SDK 内部懒加载），跳过不影响扫描。
      }
    }
  }
  return found;
};

const parseVideoMLines = (sdp: string): Array<Record<string, unknown>> => {
  const sections = sdp.split(/^m=/m).map((part, index) => (index === 0 ? part : `m=${part}`));
  const videoSections = sections.filter((section) => section.startsWith('m=video'));
  return videoSections.map((section) => {
    const direction =
      ['sendrecv', 'sendonly', 'recvonly', 'inactive'].find((dir) => new RegExp(`^a=${dir}\\b`, 'm').test(section)) ??
      'unspecified';
    const midMatch = section.match(/^a=mid:(\S+)/m);
    const ssrcMatch = section.match(/^a=ssrc:(\d+)/m);
    const msidMatch = section.match(/^a=msid:(\S+)/m);
    return { mid: midMatch?.[1], direction, ssrc: ssrcMatch?.[1], msid: msidMatch?.[1] };
  });
};

// 诊断决定性证据：本地 SDP 里各 video m-line 的协商终态。
// publisher 层每次都健康（Published/track live），但对端时好时坏。唯一能解释「本端在发字节却收不到」
// 的前端侧机制，是屏幕那条 m-line 的方向不是 sendonly（例如 stop 把它置 inactive 后，下一轮 start 复用了
// 同一条 m-line 却没重新激活）。对比「好」和「坏」两次里 ssrc 匹配 uplink_detected 的那条 m-line 方向：
// - 坏的那次 direction=inactive/recvonly → 前端/SDK 协商 bug，前端侧可修
// - 好坏两次 direction 都 sendonly → 前端已洗清，根因在下游（RTC 服务器路由 / 后端订阅）
const describeScreenSdpMLine = (engine: AliRtcEngine): Record<string, unknown> => {
  try {
    const pcs = findPeerConnections(engine);
    if (pcs.length === 0) {
      return { sdp_available: false, reason: 'no_peer_connection_found' };
    }

    // 每个 pc 都 dump video m-line，多路时全列出来，人工对照 ssrc 即可锁定屏幕那路。
    const connections = pcs.map((pc) => {
      const sdp = pc.localDescription?.sdp;
      return {
        signaling_state: pc.signalingState,
        connection_state: pc.connectionState,
        has_local_sdp: Boolean(sdp),
        video_mlines: sdp ? parseVideoMLines(sdp) : [],
      };
    });

    return { sdp_available: true, pc_count: pcs.length, connections };
  } catch (err) {
    return { sdp_available: false, read_error: err instanceof Error ? err.message : String(err) };
  }
};

// AliRtcPublishState 枚举 → 可读名，让 screenSharePublishStateChanged 日志显示 Published 而非裸数字 3。
// 与 SDK 的 AliRtcPublishState 保持一致：0 Idle / 1 NoPublish / 2 Publishing / 3 Published / 4 Reconnecting。
const PUBLISH_STATE_NAMES: Record<number, string> = {
  0: 'Idle',
  1: 'NoPublish',
  2: 'Publishing',
  3: 'Published',
  4: 'Reconnecting',
};

const describePublishState = (state: number): string => PUBLISH_STATE_NAMES[state] ?? `Unknown(${state})`;

const describeLocalVideoTrack = (track: number | undefined, rtcSdk: RtcSdkModule): string => {
  if (track === rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackCamera) {
    return 'camera';
  }
  if (track === rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackScreen) {
    return 'screen';
  }
  if (track === rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackNo) {
    return 'none';
  }
  return track === undefined ? 'unknown' : `track_${track}`;
};

const isBrowserScreenShareSupported = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  if (!window.isSecureContext) {
    return false;
  }
  return typeof navigator.mediaDevices?.getDisplayMedia === 'function';
};

const isUserCancelledScreenShareError = (err: unknown): boolean => {
  if (!(err instanceof Error)) {
    return false;
  }
  return err.name === 'NotAllowedError' || err.name === 'AbortError';
};

interface RtcMediaTrackPresence {
  hasAudioTrack: boolean;
  hasVideoTrack: boolean;
  uid: string;
}

interface RtcMediaStatsSnapshot {
  audioBytesReceived?: number;
  audioPacketsReceived?: number;
  hasAudioTrack?: boolean;
  hasVideoTrack?: boolean;
  sampledAtMs: number;
  subscribeLoss?: number;
  subscribeQuality?: number;
  subscribeRttMs?: number;
  uid: string | null;
  videoBytesReceived?: number;
  videoDecodeFps?: number;
  videoFramesDecoded?: number;
  videoFramesReceived?: number;
  videoFramesRendered?: number;
  videoFrameHeight?: number;
  videoFrameWidth?: number;
  videoPacketsReceived?: number;
}

const LOCAL_CAMERA_FRAME_RATE = 24;
const LOCAL_AUDIO_RESTORE_RETRY_DELAYS_MS = [0, 1_000, 3_000] as const;

// 屏幕采集上行分辨率上限 540p（16:9 = 960×540）。后端 OnRemoteVideoSample 拿到的是解码后裸帧，
// 体积主要由分辨率决定（RTC 网络压缩到不了业务侧）。原生 screen 辅流通过
// setScreenShareConfiguration 设编码上限，采集侧 clampScreenShareTrackConstraints 作为兜底。
const SCREEN_SHARE_MAX_WIDTH = 960;
const SCREEN_SHARE_MAX_HEIGHT = 540;
const SCREEN_SHARE_FRAME_RATE = 24;
const SCREEN_SHARE_VIDEO_CONSTRAINTS = {
  width: { max: SCREEN_SHARE_MAX_WIDTH },
  height: { max: SCREEN_SHARE_MAX_HEIGHT },
  frameRate: { ideal: SCREEN_SHARE_FRAME_RATE, max: SCREEN_SHARE_FRAME_RATE },
} as const;

// rtcLocalVideoStats 每约 2s 触发一次；每 5 次采样打一条心跳，即约每 10s 一条正向「仍在推送」证据，
// 既能确认持续推流又不刷屏。
const SCREEN_SHARE_UPLINK_HEARTBEAT_INTERVAL = 5;

// 静态画面时编码器只发极小的 P 帧，单次采样 bytesSent 可能几乎不涨。只有连续多次采样零增长
// （约 6s 内一个字节都没发）才判定为真卡死，避免把「屏幕没变化」误报成 uplink_stalled。
const SCREEN_SHARE_UPLINK_STALL_SAMPLES = 3;

// 屏幕共享内容取向。默认屏幕采集偏 detail/text，重清晰、省帧率，静态画面几乎不出帧；
// 设为 motion 让编码器倾向维持帧率、静态时也周期性重发帧，确保对端持续收到画面。
const SCREEN_SHARE_CONTENT_HINT = 'motion';

const clampScreenShareTrackConstraints = async (track: MediaStreamTrack): Promise<void> => {
  if (typeof track.applyConstraints !== 'function') {
    return;
  }

  try {
    await track.applyConstraints(SCREEN_SHARE_VIDEO_CONSTRAINTS);
  } catch {
    // 约束失败不阻断共享；部分浏览器/SDK 路径可能忽略或稍后覆盖分辨率。
  }
};

const isRtcConnectedStatus = (rtcSdk: RtcSdkModule, status: number | string): boolean => {
  return status === rtcSdk.AliRtcConnectionStatus.AliRtcConnectionStatusConnected;
};

const isRtcFailureStatus = (rtcSdk: RtcSdkModule, status: number | string): boolean => {
  return status === rtcSdk.AliRtcConnectionStatus.AliRtcConnectionStatusFailed;
};

const isRtcReconnectingStatus = (rtcSdk: RtcSdkModule, status: number | string): boolean => {
  return status === rtcSdk.AliRtcConnectionStatus.AliRtcConnectionStatusReconnecting;
};

interface LiveRtcSessionParams {
  audioEnabled?: boolean;
  /** 编辑直播允许麦克风授权弹窗与视频发布并行，普通直播保持原有串行初始化。 */
  deferLocalAudioInitialization?: boolean;
  localVideoStream?: MediaStream | null;
  localVideoSource?: string | null;
  loadRtcSdk?: () => Promise<RtcSdkModule>;
  logger?: LiveDebugLogger;
  onAutoPlayBlocked: (kind: AutoPlayBlockedKind) => void;
  onCameraError: (message: string | null) => void;
  onConnectionEstablished: () => void;
  onConnectionFailure: () => void;
  onRtcUnsupported?: () => void;
  onConnectionRecovering?: () => void;
  onLocalAudioEnabledChange: (enabled: boolean) => void;
  onLocalAudioRestoreError?: (message: string | null) => void;
  onLocalCameraEnabledChange?: (enabled: boolean) => void;
  onLocalScreenShareActiveChange?: (active: boolean) => void;
  onRemoteMediaStatsChange: (stats: RtcMediaStatsSnapshot) => void;
  onRemoteTrackPresenceChange: (presence: RtcMediaTrackPresence) => void;
  onRemoteBindingsChange: (bindings: RemoteVideoBinding[]) => void;
  onScreenShareError?: (message: string | null) => void;
  onScreenShareSupportedChange?: (supported: boolean) => void;
}

interface StartOptions {
  rtcAuthInfo: ReturnType<typeof import('./live-call').parseRtcAuthInfo>;
  rtcUserId: string;
  token: string;
}

export class LiveRtcSession {
  private readonly audioEnabled: boolean;
  private readonly deferLocalAudioInitialization: boolean;
  private localVideoStream: MediaStream | null;
  private localVideoSource: string | null;
  private readonly loadRtcSdk: () => Promise<RtcSdkModule>;
  private readonly logger?: LiveDebugLogger;
  private readonly onAutoPlayBlocked: LiveRtcSessionParams['onAutoPlayBlocked'];
  private readonly onCameraError: LiveRtcSessionParams['onCameraError'];
  private readonly onConnectionEstablished: LiveRtcSessionParams['onConnectionEstablished'];
  private readonly onConnectionFailure: LiveRtcSessionParams['onConnectionFailure'];
  private readonly onRtcUnsupported: NonNullable<LiveRtcSessionParams['onRtcUnsupported']>;
  private readonly onConnectionRecovering: NonNullable<LiveRtcSessionParams['onConnectionRecovering']>;
  private readonly onLocalAudioEnabledChange: LiveRtcSessionParams['onLocalAudioEnabledChange'];
  private readonly onLocalAudioRestoreError: NonNullable<LiveRtcSessionParams['onLocalAudioRestoreError']>;
  private readonly onLocalCameraEnabledChange: NonNullable<LiveRtcSessionParams['onLocalCameraEnabledChange']>;
  private readonly onLocalScreenShareActiveChange: NonNullable<LiveRtcSessionParams['onLocalScreenShareActiveChange']>;
  private readonly onScreenShareError: NonNullable<LiveRtcSessionParams['onScreenShareError']>;
  private readonly onScreenShareSupportedChange: NonNullable<LiveRtcSessionParams['onScreenShareSupportedChange']>;
  private readonly onRemoteMediaStatsChange: LiveRtcSessionParams['onRemoteMediaStatsChange'];
  private readonly onRemoteTrackPresenceChange: LiveRtcSessionParams['onRemoteTrackPresenceChange'];
  private readonly onRemoteBindingsChange: LiveRtcSessionParams['onRemoteBindingsChange'];

  private autoPlayBlockedAudioUsers = new Set<string>();
  private engine: AliRtcEngine | null = null;
  private disposed = false;
  private desiredLocalAudioEnabled = true;
  private desiredLocalCameraEnabled = false;
  private isScreenShareActive = false;
  private localVideoElement: HTMLVideoElement | null = null;
  private lastLoggedConnectionStatus: { status: LoggedRtcConnectionStatus; statusCode: number | string } | null = null;
  private remoteBindings: RemoteVideoBinding[] = [];
  private remoteVideoElement: HTMLVideoElement | null = null;
  /** 上次真正传给 setRemoteViewConfig 的组合，用于跳过重复重绑（见 applyRemoteView） */
  private appliedRemoteView: {
    element: HTMLVideoElement;
    uid: string;
    videoTrack: RemoteVideoBinding['videoTrack'];
  } | null = null;
  private rtcSdk: RtcSdkModule | null = null;
  private screenShareStream: MediaStream | null = null;
  private screenShareTrack: MediaStreamTrack | null = null;
  private screenShareTrackEndedListener: (() => void) | null = null;
  // 屏幕共享上行监测（rtcLocalVideoStats）：记录上次采样，只在「出现/消失/发送停滞」时打点，避免每 tick 刷屏。
  private screenShareLastPublishState: number | null = null;
  private screenShareLastBytesSent: number | null = null;
  private screenShareStalledLogged = false;
  private screenShareStatsSampleCount = 0;
  private screenShareZeroGrowthStreak = 0;

  constructor({
    audioEnabled = true,
    deferLocalAudioInitialization = false,
    localVideoStream = null,
    localVideoSource = null,
    loadRtcSdk = () => import('aliyun-rtc-sdk'),
    logger,
    onAutoPlayBlocked,
    onCameraError,
    onConnectionEstablished,
    onConnectionFailure,
    onRtcUnsupported = () => {},
    onConnectionRecovering = () => {},
    onLocalAudioEnabledChange,
    onLocalAudioRestoreError = () => {},
    onLocalCameraEnabledChange = () => {},
    onLocalScreenShareActiveChange = () => {},
    onRemoteMediaStatsChange,
    onRemoteTrackPresenceChange,
    onRemoteBindingsChange,
    onScreenShareError = () => {},
    onScreenShareSupportedChange = () => {},
  }: LiveRtcSessionParams) {
    this.audioEnabled = audioEnabled;
    this.deferLocalAudioInitialization = deferLocalAudioInitialization;
    this.localVideoStream = localVideoStream;
    this.localVideoSource = localVideoSource;
    this.loadRtcSdk = loadRtcSdk;
    this.logger = logger;
    this.onAutoPlayBlocked = onAutoPlayBlocked;
    this.onCameraError = onCameraError;
    this.onConnectionEstablished = onConnectionEstablished;
    this.onConnectionFailure = onConnectionFailure;
    this.onRtcUnsupported = onRtcUnsupported;
    this.onConnectionRecovering = onConnectionRecovering;
    this.onLocalAudioEnabledChange = onLocalAudioEnabledChange;
    this.onLocalAudioRestoreError = onLocalAudioRestoreError;
    this.onLocalCameraEnabledChange = onLocalCameraEnabledChange;
    this.onLocalScreenShareActiveChange = onLocalScreenShareActiveChange;
    this.onRemoteMediaStatsChange = onRemoteMediaStatsChange;
    this.onRemoteTrackPresenceChange = onRemoteTrackPresenceChange;
    this.onRemoteBindingsChange = onRemoteBindingsChange;
    this.onScreenShareError = onScreenShareError;
    this.onScreenShareSupportedChange = onScreenShareSupportedChange;
  }

  bindRemoteView(element: HTMLVideoElement | null): void {
    /*
     * 元素换了（含解绑传 null）就作废缓存。
     *
     * 不清会有个隐蔽的坑：解绑时 applyRemoteView 因 `!element` 提前返回、缓存
     * 仍留着旧 element；等同一个 element 再绑回来（React 复用 DOM 节点时很常见），
     * 三个字段全都相等 → 被判成"没变"而跳过 setRemoteViewConfig，画面就再也
     * 出不来了。
     */
    if (this.remoteVideoElement !== element) {
      this.appliedRemoteView = null;
    }
    this.remoteVideoElement = element;
    this.applyRemoteView();
  }

  /**
   * 回到前台后重新拉起远端画面。
   *
   * iOS WebView 切后台会暂停 video 元素的解码，回来时不会自己恢复；而 socket
   * 和 RTC 连接都还活着，所以现有的重连逻辑（handleForegroundReconnect）会被
   * `isCallReady` 守卫挡掉、什么都不做 —— 表现就是「画面一直黑，但交互正常」。
   * Android 的 WebView 会自动续播，所以只在 iOS 上能看到。
   *
   * 这里不重建连接，只做三件轻量的事：重新绑定 view、resume 远端流、把
   * <video> 再 play 一次。都是幂等的，画面没黑时调用也无副作用。
   */
  resumeRemoteView(): void {
    const engine = this.engine;
    const element = this.remoteVideoElement;
    if (this.disposed || !engine) {
      return;
    }

    /*
     * 画面还在正常播就什么都不做。
     *
     * 主要是为了 Android：它回前台时视频自动续播（paused=false），完全不需要
     * 唤醒，跑一遍 resumeRemoteMediaStream 反而让 SDK 重新挂一次轨道、白闪一下。
     * iOS 被暂停的（paused=true）才会往下走。
     *
     * 判 readyState 是因为 paused=false 也可能是"在缓冲、还没有可播数据"，
     * 那种情况该继续往下唤醒。HAVE_CURRENT_DATA(2) 表示当前帧已可用。
     */
    if (element && !element.paused && element.readyState >= 2) {
      return;
    }

    // 重新把 track 绑到 video 元素：切后台期间绑定可能已经失效
    this.applyRemoteView();

    const binding = pickDisplayBinding(this.remoteBindings);
    if (binding) {
      try {
        engine.resumeRemoteMediaStream(binding.uid, binding.videoTrack);
      } catch (error) {
        this.logger?.log('rtc', 'rtc_resume_remote_failed', {
          ...describeError(error),
        });
      }
    }

    /*
     * video 元素自身也要 play 一次：iOS 上它是被"暂停"而不是"断流"，
     * 光 resume 远端流不会让它继续解码。play() 可能因自动播放策略被拒，
     * 那种情况已有 remoteVideoAutoPlayFail → onAutoPlayBlocked 的兜底 UI。
     */
    if (element?.paused) {
      void element.play().catch((error: unknown) => {
        this.logger?.log('rtc', 'rtc_resume_video_play_failed', {
          ...describeError(error),
        });
      });
    }
  }

  /**
   * 远端 <video> 的播放实况，给 autoplay 日志做旁证。
   *
   * 没绑到元素时返回 `remote_video_bound: false` 而不是省略字段——查日志时
   * "没有这个字段"和"字段是 false"很难分辨，显式给出来省一轮猜测。
   */
  private describeRemoteVideoElement(): Record<string, LiveDebugValue> {
    const element = this.remoteVideoElement;
    if (!element) {
      return { remote_video_bound: false };
    }

    return {
      remote_video_bound: true,
      remote_video_paused: element.paused,
      remote_video_muted: element.muted,
      remote_video_ready_state: element.readyState,
      remote_video_current_time_ms: Math.round(element.currentTime * 1000),
    };
  }

  bindLocalView(element: HTMLVideoElement | null): void {
    this.localVideoElement = element;
    if (!element || !this.engine || (!this.localVideoStream && !this.desiredLocalCameraEnabled)) {
      return;
    }
    void this.applyLocalView(element);
  }

  setLocalVideoSource(source: string | null | undefined): void {
    this.localVideoSource = source ?? null;
  }

  private async applyLocalView(element: HTMLVideoElement): Promise<void> {
    const engine = this.engine;
    if (this.disposed || !engine || this.localVideoElement !== element) {
      return;
    }

    try {
      const rtcSdk = await this.getRtcSdk();
      if (this.disposed || this.engine !== engine || this.localVideoElement !== element) {
        return;
      }
      await engine.setLocalViewConfig(element, rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackCamera);
    } catch {
      // Preview binding failure must not interrupt an already active RTC publish session.
    }
  }

  async start({ rtcAuthInfo, rtcUserId, token }: StartOptions): Promise<void> {
    if (this.disposed || this.engine) {
      return;
    }

    this.logger?.mark('rtc_start');
    this.lastLoggedConnectionStatus = null;
    this.logger?.log('rtc', 'rtc_start', {
      elapsed_ms_since_session_start: this.logger?.since('session_start'),
    });

    const support = getRtcSupportStatus();
    if (!support.supported) {
      this.logger?.log('rtc', 'rtc_unsupported', {
        rtc_vendor: 'aliyun',
        ...support,
      });
      this.onRtcUnsupported();
      return;
    }

    let engine: AliRtcEngine | null = null;
    let failureStage = 'teardown_wait';

    try {
      await waitForRtcEngineTeardown();
      failureStage = 'sdk_load';
      const rtcSdk = await this.getRtcSdk();
      failureStage = 'screen_share_probe';
      await this.probeScreenShareSupport(rtcSdk);
      // enableBlankAudio: 开启后 SDK 内部 BlankAudioPlugin 会在 mute 期间持续往上行推空白音频，
      // 保持音频轨道活跃，避免对端 AI 因 6s 收不到 audio packet 触发 INBOUND_TIMEOUT
      // 判定用户掉线。等价于 iOS 端的 whiteNoiseAudioPusher，但由 SDK 自身托管，
      // 不需要我们手动 switchMicrophone 换 noise track。
      failureStage = 'engine_create';
      const activeEngine = rtcSdk.default.getInstance({
        parameter: { enableBlankAudio: true },
      });
      engine = activeEngine;
      this.engine = activeEngine;
      failureStage = 'engine_configure';
      activeEngine.setChannelProfile(rtcSdk.AliRtcSdkChannelProfile.AliRtcSdkInteractiveLive);
      await activeEngine.setClientRole(rtcSdk.AliRtcSdkClientRole.AliRtcSdkInteractive);
      await activeEngine.setAudioProfile('standard');
      activeEngine.setPlayoutVolume(100);
      activeEngine.setRecordingVolume(100);
      activeEngine.muteAllRemoteAudioPlaying(!this.audioEnabled);
      let shouldInitializeLocalAudioAfterVideo = false;
      if (this.audioEnabled) {
        if (this.deferLocalAudioInitialization) {
          // 编辑直播先发布视频，再启动麦克风；SDK 的音频采集调用可能占用内部媒体锁，
          // 即使不 await 也会让后续视频发布排队，导致权限弹窗未处理时画面迟迟不出来。
          shouldInitializeLocalAudioAfterVideo = true;
        } else {
          await this.initializeLocalAudio(activeEngine);
        }
      } else {
        await activeEngine.publishLocalAudioStream(false);
        this.onLocalAudioEnabledChange(false);
      }
      if (this.localVideoStream) {
        const publisher = activeEngine.publisher as unknown as
          | {
              createLocalStream?: (options: {
                audio?: boolean;
                mediaStream?: MediaStream;
                video?: boolean;
              }) => Promise<void>;
            }
          | undefined;
        if (publisher?.createLocalStream) {
          await publisher.createLocalStream({
            audio: false,
            mediaStream: this.localVideoStream,
            video: true,
          });
        }
        if (this.localVideoElement) {
          await activeEngine.setLocalViewConfig(this.localVideoElement, rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackCamera);
        }
        await activeEngine.publishLocalVideoStream(true);
        // 首屏耗时锚点之一：毫秒级精确，但语义是「已发起推流」，不保证帧已编码发出，
        // 会偏乐观。真实第一帧上行用 local_first_frame_sent（由 rtcLocalVideoStats 判定）。
        this.logger?.markOnce('rtc_publish_called');
        this.logger?.logOnce('rtc_publish_called', 'rtc', 'rtc_publish_called', {
          input_source: this.localVideoSource,
          elapsed_ms_since_rtc_join_channel_done: this.logger?.since('rtc_join_channel_done'),
          elapsed_ms_since_session_start: this.logger?.since('session_start'),
        });
      } else {
        activeEngine.publishLocalVideoStream(false);
      }
      this.onLocalCameraEnabledChange(this.desiredLocalCameraEnabled);
      activeEngine.setDefaultSubscribeAllRemoteAudioStreams(this.audioEnabled);
      activeEngine.subscribeAllRemoteAudioStreams(this.audioEnabled);
      activeEngine.setDefaultSubscribeAllRemoteVideoStreams(true);
      activeEngine.subscribeAllRemoteVideoStreams(true);

      activeEngine.on('connectionStatusChange', (status) => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }

        if (isRtcConnectedStatus(rtcSdk, status)) {
          this.logConnectionStatus('connected', status);
          this.onConnectionEstablished();
          return;
        }

        if (isRtcReconnectingStatus(rtcSdk, status)) {
          this.logConnectionStatus('reconnecting', status);
          this.onConnectionRecovering();
          return;
        }

        if (isRtcFailureStatus(rtcSdk, status)) {
          this.logConnectionStatus('failed', status);
          this.onConnectionFailure();
        }
      });

      activeEngine.on('remoteUserOffLineNotify', (uid: string) => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }

        this.autoPlayBlockedAudioUsers.delete(uid);
        this.removeRemoteBinding(uid);
      });

      activeEngine.on('remoteTrackAvailableNotify', (uid, audioTrack, videoTrack) => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }

        const hasAudio = audioTrack !== rtcSdk.AliRtcAudioTrack.AliRtcAudioTrackNo;
        const hasVideo = videoTrack !== rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackNo;
        this.logger?.log('rtc', 'rtc_remote_track', {
          has_audio: hasAudio,
          has_video: hasVideo,
          elapsed_ms_since_rtc_join_channel_done: this.logger?.since('rtc_join_channel_done'),
          elapsed_ms_since_session_start: this.logger?.since('session_start'),
        });

        this.onRemoteTrackPresenceChange({
          hasAudioTrack: hasAudio,
          hasVideoTrack: hasVideo,
          uid,
        });

        if (videoTrack === rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackNo) {
          this.removeRemoteBinding(uid);
        } else {
          activeEngine.subscribeRemoteMediaStream(
            uid,
            videoTrack,
            true,
            audioTrack !== rtcSdk.AliRtcAudioTrack.AliRtcAudioTrackNo,
          );
          activeEngine.resumeRemoteMediaStream(uid, videoTrack);
          this.upsertRemoteBinding({ uid, audioTrack, videoTrack });
        }

        if (this.audioEnabled && audioTrack !== rtcSdk.AliRtcAudioTrack.AliRtcAudioTrackNo) {
          activeEngine.subscribeRemoteAudioStream(uid, true);
          activeEngine.muteRemoteAudioPlaying(uid, false);
          activeEngine.setRemoteAudioVolume(uid, 100);
          activeEngine.resumeRemoteAudioStream(uid);
        }
      });

      activeEngine.on('remoteAudioAutoPlayFail', (uid: string) => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }

        this.autoPlayBlockedAudioUsers.add(uid);
        /*
         * 带上 <video> 元素的实况。
         *
         * SDK 的音频走它自己 new 出来的 <audio>，我们拿不到；但远端画面和声音
         * 同源，video 元素在不在播是判断「这次拦截是否真的影响到用户」的最近
         * 旁证。排查「声音视频都正常却弹提示」时，这里 paused=false 就说明
         * 至少视频侧没被拦，问题范围能立刻收敛到音频。
         */
        this.logger?.log('rtc', 'rtc_autoplay_blocked', {
          kind: 'audio',
          ...this.describeRemoteVideoElement(),
        });
        this.onAutoPlayBlocked('audio');
      });

      activeEngine.on('remoteVideoAutoPlayFail', () => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }

        this.logger?.log('rtc', 'rtc_autoplay_blocked', {
          kind: 'video',
          ...this.describeRemoteVideoElement(),
        });
        this.onAutoPlayBlocked('video');
      });

      activeEngine.on('rtcRemoteVideoStats', (stats: Array<{ stats?: Record<string, unknown>; uid?: string }>) => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }

        const primary = stats[0];
        if (!primary?.uid) {
          return;
        }

        this.onRemoteMediaStatsChange({
          hasVideoTrack: true,
          sampledAtMs: Date.now(),
          uid: primary.uid,
          videoBytesReceived: Number(primary.stats?.bytesReceived ?? 0),
          videoDecodeFps: Number(primary.stats?.framesPerSecond ?? 0),
          videoFramesDecoded: Number(primary.stats?.framesDecoded ?? 0),
          videoFramesReceived: Number(primary.stats?.framesReceived ?? 0),
          videoFramesRendered: Number(primary.stats?.framesRendered ?? 0),
          videoFrameHeight: Number(primary.stats?.frameHeight ?? 0),
          videoFrameWidth: Number(primary.stats?.frameWidth ?? 0),
          videoPacketsReceived: Number(primary.stats?.packetsReceived ?? 0),
        });
      });

      activeEngine.on('rtcRemoteAudioStats', (stats: Array<{ stats?: Record<string, unknown>; uid?: string }>) => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }

        const primary = stats[0];
        if (!primary?.uid) {
          return;
        }

        this.onRemoteMediaStatsChange({
          audioBytesReceived: Number(primary.stats?.bytesReceived ?? 0),
          audioPacketsReceived: Number(primary.stats?.packetsReceived ?? 0),
          hasAudioTrack: true,
          sampledAtMs: Date.now(),
          uid: primary.uid,
        });
      });

      activeEngine.on('localDeviceException', (deviceType, exceptionType, description) => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }

        const isDisplayDevice =
          deviceType === rtcSdk.AliRtcEngineLocalDeviceType.AliEngineLocalDeviceTypeDisplay ||
          exceptionType ===
            rtcSdk.AliRtcEngineLocalDeviceExceptionType.AliEngineLocalDeviceExceptionTypeDisplayException;
        if (!isDisplayDevice) {
          return;
        }

        warnScreenShare('localDeviceException', { deviceType, description, exceptionType });

        if (this.isScreenShareActive) {
          this.handleScreenShareInterrupted().catch(() => {});
        }
      });

      activeEngine.on('networkQuality', (event: { subscribe?: { loss?: number; quality?: number; rtt?: number } }) => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }

        this.onRemoteMediaStatsChange({
          sampledAtMs: Date.now(),
          subscribeLoss: event.subscribe?.loss,
          subscribeQuality: event.subscribe?.quality,
          subscribeRttMs: event.subscribe?.rtt,
          uid: null,
        });
      });

      // 层①：屏幕共享推流状态的权威信号。newState=3(Published) 才代表推流链路真正建立成功。
      // 停在 Publishing / 落到 NoPublish 即定位到推流环节失败，而非采集或对端问题。
      activeEngine.on(
        'screenSharePublishStateChanged',
        (oldState: number, newState: number, elapseSinceLastState: number) => {
          if (this.disposed || this.engine !== activeEngine) {
            return;
          }
          this.screenShareLastPublishState = newState;
          infoScreenShare('publish_state_changed', {
            old_state: describePublishState(oldState),
            new_state: describePublishState(newState),
            elapse_ms: elapseSinceLastState,
          });
        },
      );

      // rtcLocalVideoStats 会返回所有本地视频轨道。通用日志用于确认摄像头、上传视频、
      // 图片捕获视频和屏幕共享是否真的有上行发送；屏幕轨道随后再进入专用诊断。
      activeEngine.on('rtcLocalVideoStats', (stats: Array<{ track?: number; stats?: Record<string, unknown> }>) => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }
        for (const item of stats) {
          const itemStats = item.stats ?? {};
          const framesEncoded = Number(itemStats.framesEncoded ?? 0);
          const bytesSent = Number(itemStats.bytesSent ?? 0);
          this.logger?.log('rtc', 'local_video_stats', {
            input_source: this.localVideoSource,
            track: describeLocalVideoTrack(item.track, rtcSdk),
            track_type: item.track,
            bytes_sent: bytesSent,
            frames_encoded: framesEncoded,
            frames_per_second: Number(itemStats.framesPerSecond ?? 0),
            video_frame_height: Number(itemStats.frameHeight ?? 0),
            video_frame_width: Number(itemStats.frameWidth ?? 0),
            ssrc: typeof itemStats.ssrc === 'string' || typeof itemStats.ssrc === 'number' ? itemStats.ssrc : undefined,
            rid: typeof itemStats.rid === 'string' ? itemStats.rid : undefined,
            mid: typeof itemStats.mid === 'string' ? itemStats.mid : undefined,
          });

          /*
           * 首屏耗时的真实起点：第一帧确实编码上行了才算「开始推流」。
           * publishLocalVideoStream 返回只代表发起推流，帧可能还没出去。
           *
           * 注意精度：rtcLocalVideoStats 约 2s 一次采样，所以这个时刻最大有 ~2s 滞后
           * （真实第一帧落在两次采样之间）。配合 rtc_publish_called 一起看：
           * 两者差值异常大就说明上行卡住了，而不是服务端慢。
           */
          if (framesEncoded > 0 || bytesSent > 0) {
            this.logger?.markOnce('local_first_frame_sent');
            this.logger?.logOnce('local_first_frame_sent', 'rtc', 'local_first_frame_sent', {
              input_source: this.localVideoSource,
              track: describeLocalVideoTrack(item.track, rtcSdk),
              frames_encoded: framesEncoded,
              bytes_sent: bytesSent,
              elapsed_ms_since_rtc_publish_called: this.logger?.since('rtc_publish_called'),
              elapsed_ms_since_rtc_join_channel_done: this.logger?.since('rtc_join_channel_done'),
              elapsed_ms_since_session_start: this.logger?.since('session_start'),
            });
          }
        }
        this.handleScreenShareUplinkStats(rtcSdk, stats);
      });

      activeEngine.on('occurError', (error, uid) => {
        if (this.disposed || this.engine !== activeEngine) {
          return;
        }

        this.logger?.log('rtc', 'rtc_sdk_error', {
          uid,
          ...describeError(error, { includeMessage: true }),
        });
      });

      failureStage = 'join_channel';
      this.logger?.mark('rtc_join_channel_start');
      this.logger?.log('rtc', 'rtc_join_channel_start', {
        has_rtc_auth_info: Boolean(rtcAuthInfo),
        elapsed_ms_since_rtc_start: this.logger?.since('rtc_start'),
        elapsed_ms_since_session_start: this.logger?.since('session_start'),
      });
      if (rtcAuthInfo) {
        await activeEngine.joinChannel(rtcAuthInfo, { userName: rtcUserId });
      } else {
        await activeEngine.joinChannel(token, { userName: rtcUserId });
      }
      const elapsedMsSinceRtcJoinChannelStart = this.logger?.since('rtc_join_channel_start');
      this.logger?.mark('rtc_join_channel_done');
      this.logger?.log('rtc', 'rtc_join_channel_done', {
        success: true,
        elapsed_ms_since_rtc_join_channel_start: elapsedMsSinceRtcJoinChannelStart,
        elapsed_ms_since_session_start: this.logger?.since('session_start'),
      });

      if (shouldInitializeLocalAudioAfterVideo) {
        // 进房后再采麦，避免权限弹窗卡住 join；但必须在本地摄像头预览播放前完成，
        // 否则 Chrome 会把 AEC 锁到高延迟路径（/live-pro 默认关摄像头所以没有这个问题）。
        await this.initializeLocalAudio(activeEngine);
      }
      if (this.desiredLocalCameraEnabled) {
        await this.enableLocalCamera(activeEngine, null);
      }
    } catch (error) {
      this.logger?.log('rtc', 'rtc_start_failed', {
        failure_stage: failureStage,
        elapsed_ms_since_rtc_start: this.logger?.since('rtc_start'),
        elapsed_ms_since_rtc_join_channel_start: this.logger?.since('rtc_join_channel_start'),
        elapsed_ms_since_session_start: this.logger?.since('session_start'),
        ...describeError(error, { includeMessage: true }),
      });
      if (this.engine === engine) {
        this.engine = null;
      }
      if (engine) {
        await teardownRtcEngine(engine);
      }
      if (!this.disposed) {
        this.onConnectionFailure();
      }
    }
  }

  async stop(): Promise<void> {
    const engine = this.engine;
    this.engine = null;
    this.remoteBindings = [];
    this.onRemoteBindingsChange([]);
    this.autoPlayBlockedAudioUsers.clear();
    // 引擎换了之后 SDK 侧的绑定全部失效，缓存必须清掉，否则重连后会跳过必要的重绑
    this.appliedRemoteView = null;
    this.detachScreenShareTrackListener();

    if (!engine) {
      this.setScreenShareActive(false);
      return;
    }

    if (this.isScreenShareActive) {
      await this.stopScreenShare(engine);
    }

    await teardownRtcEngine(engine);
  }

  async toggleLocalAudio(currentValue: boolean): Promise<void> {
    const nextValue = !currentValue;
    this.desiredLocalAudioEnabled = nextValue;

    const engine = this.engine;
    if (!engine) {
      return;
    }

    try {
      if (nextValue) {
        this.pauseLocalCapturePlayback();
        try {
          await warmupRtcMicrophoneIfNeeded();
          if (this.disposed || this.engine !== engine) {
            return;
          }
          await engine.startAudioCapture({
            autoGainControl: true,
            echoCancellation: true,
            noiseSuppression: true,
          });
          await engine.publishLocalAudioStream(true);
        } finally {
          this.resumeLocalCapturePlayback();
        }
      }
      // 只切 mic 静音开关。mute 期间 SDK 自身的 BlankAudioPlugin
      // （engine 创建时 enableBlankAudio: true 启用）会持续往上行推空白音频，
      // 保持轨道活跃，避免对端 AI 判掉线。unmute 时也无需额外处理。
      engine.muteLocalMic(!nextValue);
      this.onLocalAudioEnabledChange(nextValue);
      this.onLocalAudioRestoreError(null);
    } catch (error) {
      this.desiredLocalAudioEnabled = false;
      this.onLocalAudioEnabledChange(false);
      this.onLocalAudioRestoreError(normalizeError(error));
      throw error;
    }
  }

  async updateLocalVideoStream(stream: MediaStream | null): Promise<void> {
    const engine = this.engine;
    if (!stream) {
      this.localVideoStream = null;
      return;
    }
    if (!engine) {
      this.localVideoStream = stream;
      return;
    }
    const publisher = engine.publisher as unknown as {
      createLocalStream?: (options: {
        audio?: boolean;
        mediaStream?: MediaStream;
        video?: boolean;
      }) => Promise<unknown>;
    };
    if (!publisher.createLocalStream) return;
    await publisher.createLocalStream({
      audio: false,
      mediaStream: stream,
      video: true,
    });
    const rtcSdk = await this.getRtcSdk();
    if (this.localVideoElement) {
      await engine.setLocalViewConfig(this.localVideoElement, rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackCamera);
    }
    await engine.publishLocalVideoStream(true);
    this.localVideoStream = stream;
  }

  async toggleLocalCamera(currentValue: boolean, cameraPermissionErrorText: string): Promise<void> {
    const nextValue = !currentValue;
    this.desiredLocalCameraEnabled = nextValue;

    const engine = this.engine;
    if (!engine) {
      return;
    }

    if (!nextValue) {
      await this.disableLocalCamera(engine);
      return;
    }

    await this.enableLocalCamera(engine, cameraPermissionErrorText);
  }

  async toggleLocalScreenShare(
    currentValue: boolean,
    options: { permissionErrorText: string; unsupportedErrorText: string; startErrorText: string },
  ): Promise<void> {
    const engine = this.engine;
    if (!engine) {
      warnScreenShare('toggle_skipped', { reason: 'engine_missing' });
      return;
    }

    const nextValue = !currentValue;
    this.onScreenShareError(null);
    infoScreenShare('toggle', { current: currentValue, next: nextValue, was_active: this.isScreenShareActive });

    if (!nextValue) {
      await this.stopScreenShare(engine);
      return;
    }

    if (this.isScreenShareActive) {
      await this.stopScreenShare(engine);
    }

    if (!isBrowserScreenShareSupported()) {
      warnScreenShare('start_skipped', { reason: 'unsupported' });
      this.onScreenShareSupportedChange(false);
      this.onScreenShareError(options.unsupportedErrorText);
      return;
    }

    let capturedStream: MediaStream | null = null;
    try {
      // 先拿 display stream，保住 Safari 的 user gesture（早于任何 async SDK 检查）。
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: SCREEN_SHARE_VIDEO_CONSTRAINTS,
        audio: false,
      });
      capturedStream = stream;
      const track = stream.getVideoTracks()[0];
      if (!track) {
        throw new Error('screen video track missing');
      }
      infoScreenShare('display_track_captured', describeScreenShareTrack(track));
      await clampScreenShareTrackConstraints(track);
      // 采集 track 直接标注 motion：静态画面也维持帧率、持续出帧，避免对端画面卡在旧帧。
      try {
        track.contentHint = SCREEN_SHARE_CONTENT_HINT;
      } catch {
        // contentHint 是标准可写属性；个别环境不支持时忽略，不阻断共享。
      }
      infoScreenShare('display_track_clamped', describeScreenShareTrack(track));

      const rtcSdk = await this.getRtcSdk();
      const supported = await this.probeScreenShareSupport(rtcSdk);
      if (!supported) {
        warnScreenShare('start_skipped', { reason: 'unsupported' });
        this.onScreenShareError(options.unsupportedErrorText);
        stream.getTracks().forEach((streamTrack) => {
          streamTrack.stop();
        });
        capturedStream = null;
        return;
      }

      this.screenShareStream = stream;
      this.attachScreenShareTrackListener(track);
      // 屏幕共享期间只上行 screen 一路：先停 camera 上行，再起 screen 推流。
      // 放在 startScreenShare 之前，让两次 SDP 重协商顺序完成，避免 screen 协商刚 settle
      // 又被 camera 的 publish 变更打断。
      await this.pauseCameraPublishForScreenShare(engine);
      // 原生 screen 辅流：setScreenShareConfiguration 设 540p / 24 FPS 编码上限，
      // startScreenShare（单一入口）用采集到的 videoTrack 采集 + 推流。
      // 摄像头采集与预览不受影响（见 pauseCameraPublishForScreenShare），仅暂停其上行。
      // 严禁再调 publishLocalScreenShareStream：两者都触发 SDP 重协商，并发会互相打断，
      // 导致首次开启对端看不到、切两次才生效。
      await engine.setScreenShareConfiguration({
        width: SCREEN_SHARE_MAX_WIDTH,
        height: SCREEN_SHARE_MAX_HEIGHT,
        frameRate: SCREEN_SHARE_FRAME_RATE,
      });
      infoScreenShare('start_screen_share_begin', {
        max_width: SCREEN_SHARE_MAX_WIDTH,
        max_height: SCREEN_SHARE_MAX_HEIGHT,
        frame_rate: SCREEN_SHARE_FRAME_RATE,
      });
      await engine.startScreenShare({ audio: false, videoTrack: track });
      // 走 SDK 文档路径再声明一次 motion，覆盖 SDK 内部对屏幕流编码取向的默认设置。
      if (typeof engine.setScreenShareContentHint === 'function') {
        try {
          await engine.setScreenShareContentHint(SCREEN_SHARE_CONTENT_HINT);
        } catch (err) {
          warnScreenShare('set_content_hint_error', {
            error_name: err instanceof Error ? err.name : typeof err,
            error_message: err instanceof Error ? err.message : String(err),
          });
        }
      }
      this.setScreenShareActive(true);
      // startScreenShare resolve 后 track 仍应是 live；若此处 ready_state 已是 ended，
      // 说明采集在推流建立前就断了，对端自然收不到。
      infoScreenShare('start_screen_share_done', describeScreenShareTrack(track));
      // 诊断多次开关后对端收不到：对比 SDK 实际发布的 screen track 与本次采集的 track。
      infoScreenShare('publish_state_after_start', describeScreenPublishState(engine, track.id));
      // 决定性证据：本地 SDP 里 screen m-line 的方向/msid/ssrc。对比好坏两次即可定位根因在前端还是下游。
      infoScreenShare('sdp_after_start', describeScreenSdpMLine(engine));
    } catch (err) {
      if (isUserCancelledScreenShareError(err)) {
        warnScreenShare('start_cancelled', { error_name: err instanceof Error ? err.name : typeof err });
      } else {
        errorScreenShare('start_failed', err, { reason: 'sdk_error' });
      }

      await this.stopScreenShare(engine);
      if (capturedStream && capturedStream !== this.screenShareStream) {
        capturedStream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      if (!isUserCancelledScreenShareError(err)) {
        this.onScreenShareError(options.startErrorText);
      }
    }
  }

  async restoreLocalMedia(): Promise<void> {
    const engine = this.engine;
    if (!engine) {
      return;
    }

    // Screen share track relies on user gesture (getDisplayMedia). It cannot be silently
    // re-acquired after reconnect, so reconnect drops it and only restores media that is still desired.
    if (this.isScreenShareActive) {
      await this.stopScreenShare(engine);
    }

    await Promise.all([
      this.audioEnabled ? this.restoreLocalAudio(engine) : Promise.resolve(),
      this.desiredLocalCameraEnabled ? this.enableLocalCamera(engine, null) : this.disableLocalCamera(engine),
    ]);
  }

  resumePlayback(): Promise<void> | void {
    const engine = this.engine;
    if (engine) {
      engine.muteAllRemoteAudioPlaying(!this.audioEnabled);
      engine.setPlayoutVolume(this.audioEnabled ? 100 : 0);
      this.autoPlayBlockedAudioUsers.forEach((uid) => {
        engine.resumeRemoteAudioStream(uid);
      });
      this.autoPlayBlockedAudioUsers.clear();
    }

    return this.remoteVideoElement?.play();
  }

  private applyRemoteView(): void {
    const engine = this.engine;
    const element = this.remoteVideoElement;
    const binding = pickDisplayBinding(this.remoteBindings);

    if (!engine || !element || !binding) {
      return;
    }

    /*
     * 绑定没变就不重设。
     *
     * `setRemoteViewConfig` 会让 SDK 重新挂载轨道到 <video>，画面会闪一下。
     * 而本方法有多个调用点（bindRemoteView / 远端轨道通知 / upsertRemoteBinding /
     * resumeRemoteView），同一次回前台里会被连着调好几次：
     *   resumeRemoteMediaStream 恢复流 → SDK 重新推送轨道通知
     *   → handleRemoteBindingsChange → bindRemoteView → 又一次 setRemoteViewConfig
     * 每一次都闪，所以用户看到的是"闪两下"。
     *
     * 记住上次真正生效的组合，元素和轨道都没变就跳过——这些重复调用本来
     * 就是幂等意图，只是 SDK 那一侧不幂等。
     */
    if (
      this.appliedRemoteView?.element === element &&
      this.appliedRemoteView.uid === binding.uid &&
      this.appliedRemoteView.videoTrack === binding.videoTrack
    ) {
      return;
    }

    this.appliedRemoteView = {
      element,
      uid: binding.uid,
      videoTrack: binding.videoTrack,
    };
    engine.setRemoteViewConfig(element, binding.uid, binding.videoTrack);
  }

  private async getRtcSdk(): Promise<RtcSdkModule> {
    if (this.rtcSdk) {
      return this.rtcSdk;
    }

    this.rtcSdk = await this.loadRtcSdk();
    return this.rtcSdk;
  }

  private async restoreLocalAudio(engine: AliRtcEngine): Promise<void> {
    if (!this.audioEnabled) return;
    let lastError: unknown;

    this.pauseLocalCapturePlayback();
    try {
      await warmupRtcMicrophoneIfNeeded();
      if (this.disposed || this.engine !== engine) {
        return;
      }

      for (const delayMs of LOCAL_AUDIO_RESTORE_RETRY_DELAYS_MS) {
        if (delayMs > 0) {
          await new Promise<void>((resolve) => {
            setTimeout(resolve, delayMs);
          });
        }

        if (this.disposed || this.engine !== engine) {
          return;
        }

        try {
          await engine.startAudioCapture({
            autoGainControl: true,
            echoCancellation: true,
            noiseSuppression: true,
          });
          await engine.publishLocalAudioStream(true);
          engine.muteLocalMic(!this.desiredLocalAudioEnabled);
          this.onLocalAudioEnabledChange(this.desiredLocalAudioEnabled);
          this.onLocalAudioRestoreError(null);
          return;
        } catch (error) {
          lastError = error;
        }
      }

      this.onLocalAudioRestoreError(normalizeError(lastError));
    } catch (error) {
      this.onLocalAudioRestoreError(normalizeError(error));
    } finally {
      this.resumeLocalCapturePlayback();
    }
  }

  private async initializeLocalAudio(engine: AliRtcEngine): Promise<void> {
    const shouldRestoreCameraAfterCapture =
      hasMirroredCameraPlayback(this.localVideoStream) || isRtcCameraGumStream(this.localVideoStream);
    this.pauseLocalCapturePlayback();
    if (shouldRestoreCameraAfterCapture) {
      if (!stopMirroredCameraSource(this.localVideoStream)) {
        this.localVideoStream?.getTracks().forEach((track) => {
          if (track.readyState !== 'ended') {
            track.stop();
          }
        });
        this.localVideoStream = null;
      }
    }
    try {
      await warmupRtcMicrophoneIfNeeded();
      if (this.disposed || this.engine !== engine) return;
      await engine.startAudioCapture({
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
      });
      if (this.disposed || this.engine !== engine) return;
      await engine.publishLocalAudioStream(true);
      engine.muteLocalMic(!this.desiredLocalAudioEnabled);
      this.onLocalAudioEnabledChange(this.desiredLocalAudioEnabled);
    } catch (error) {
      if (this.disposed || this.engine !== engine) return;
      // 麦克风属于可选上行能力；拒绝权限时仍继续视频直播，用户稍后可主动重试。
      this.desiredLocalAudioEnabled = false;
      this.onLocalAudioEnabledChange(false);
      this.onLocalAudioRestoreError(normalizeError(error));
      await engine.publishLocalAudioStream(false);
      this.logger?.log('rtc', 'rtc_microphone_permission_denied', {
        ...describeError(error),
      });
    } finally {
      this.resumeLocalCapturePlayback();
    }
    if (shouldRestoreCameraAfterCapture && !this.disposed && this.engine === engine) {
      try {
        const captured = await openRtcCameraStream(true, undefined, { skipWarmup: true });
        if (captured && !this.disposed && this.engine === engine) {
          await this.updateLocalVideoStream(captured);
        }
      } catch {
        // 采麦后重开摄像头失败时保留已建立的音频。
      }
    }
  }

  private pauseLocalCapturePlayback(): void {
    pauseMirroredCameraPlayback(this.localVideoStream);
    const videos = new Set<HTMLVideoElement>();
    if (this.localVideoElement) {
      videos.add(this.localVideoElement);
    }
    if (typeof document !== 'undefined' && typeof document.querySelectorAll === 'function' && this.localVideoStream) {
      for (const video of document.querySelectorAll('video')) {
        if (video.srcObject === this.localVideoStream) {
          videos.add(video);
        }
      }
    }
    for (const video of videos) {
      if (video.paused) {
        continue;
      }
      try {
        video.pause();
      } catch {
        // jsdom 和部分 WebView 的 pause 会抛未实现错误。
      }
    }
  }

  private resumeLocalCapturePlayback(): void {
    resumeMirroredCameraPlayback(this.localVideoStream);
    const element = this.localVideoElement;
    if (!element?.srcObject) {
      return;
    }
    void element.play?.()?.catch(() => {});
  }

  private async enableLocalCamera(engine: AliRtcEngine, cameraPermissionErrorText: string | null): Promise<void> {
    this.onCameraError(null);

    await warmupRtcMicrophoneIfNeeded();
    if (this.disposed || this.engine !== engine) {
      return;
    }

    if (cameraPermissionErrorText) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      } catch (permissionError) {
        this.desiredLocalCameraEnabled = false;
        this.onCameraError(cameraPermissionErrorText);
        this.onLocalCameraEnabledChange(false);
        this.logger?.log('rtc', 'rtc_camera_permission_denied', {
          ...describeError(permissionError),
        });
        return;
      }
    }

    try {
      const rtcSdk = await this.getRtcSdk();
      await engine.enableLocalVideo(true);
      await engine.setCameraCapturerConfiguration({ frameRate: LOCAL_CAMERA_FRAME_RATE });
      if (this.localVideoElement) {
        await engine.setLocalViewConfig(this.localVideoElement, rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackCamera);
      }
      await engine.startPreview();
      // 屏幕共享期间只上行 screen 一路：采集与预览照常起，但不推 camera 流，
      // 否则会绕过 pauseCameraPublishForScreenShare 的暂停。共享结束后由
      // resumeCameraPublishAfterScreenShare 统一恢复。
      if (this.isScreenShareActive) {
        infoScreenShare('camera_publish_skipped_during_share');
      } else {
        await engine.publishLocalVideoStream(true);
      }
      this.onLocalCameraEnabledChange(true);
    } catch (error) {
      this.desiredLocalCameraEnabled = false;
      this.onCameraError(normalizeError(error));
      this.onLocalCameraEnabledChange(false);
      this.logger?.log('rtc', 'rtc_camera_error', {
        ...describeError(error, { includeMessage: true }),
      });
    }
  }

  private async disableLocalCamera(engine: AliRtcEngine): Promise<void> {
    try {
      await engine.publishLocalVideoStream(false);
      await engine.enableLocalVideo(false);
      await engine.stopPreview();
    } catch {
      // Do not block the rest of the call on local camera disable errors.
    }
    this.onLocalCameraEnabledChange(false);
  }

  // 屏幕共享开始时暂停 camera 上行。用 publishLocalVideoStream(false) 而非 enableLocalVideo/stopPreview：
  // 按 SDK 语义它「不会停止本地视频采集」，因此摄像头灯、本地预览小窗、camera track 全部保留，
  // 只是不再向 RTC 推 camera 流。不触碰 desiredLocalCameraEnabled 与 onLocalCameraEnabledChange，
  // 用户视角摄像头仍是开启状态。
  private async pauseCameraPublishForScreenShare(engine: AliRtcEngine): Promise<void> {
    if (!this.desiredLocalCameraEnabled) {
      return;
    }

    try {
      await engine.publishLocalVideoStream(false);
      infoScreenShare('camera_publish_paused');
    } catch (err) {
      warnScreenShare('camera_publish_pause_error', {
        error_name: err instanceof Error ? err.name : typeof err,
        error_message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  // 屏幕共享结束时恢复 camera 上行，前提是摄像头仍是开启意图。
  // 共享期间用户关掉摄像头的话 desiredLocalCameraEnabled 已为 false，这里不擅自推起来。
  private async resumeCameraPublishAfterScreenShare(engine: AliRtcEngine): Promise<void> {
    if (!this.desiredLocalCameraEnabled) {
      return;
    }

    try {
      await engine.publishLocalVideoStream(true);
      infoScreenShare('camera_publish_resumed');
    } catch (err) {
      warnScreenShare('camera_publish_resume_error', {
        error_name: err instanceof Error ? err.name : typeof err,
        error_message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private async stopScreenShare(engine: AliRtcEngine): Promise<void> {
    infoScreenShare('stop_screen_share_begin', { was_active: this.isScreenShareActive });
    this.detachScreenShareTrackListener();
    // 原生 screen 辅流收尾：stopScreenShare 停止采集与推流。摄像头 camera track 独立，不受影响。
    try {
      await engine.stopScreenShare();
    } catch (err) {
      warnScreenShare('stop_screen_share_error', {
        error_name: err instanceof Error ? err.name : typeof err,
        error_message: err instanceof Error ? err.message : String(err),
      });
    }
    this.screenShareStream?.getTracks().forEach((track) => {
      track.stop();
    });
    this.screenShareStream = null;
    this.setScreenShareActive(false);
    // 重置上行监测状态，下次开启从干净基线重新判定。
    this.screenShareLastBytesSent = null;
    this.screenShareStalledLogged = false;
    this.screenShareStatsSampleCount = 0;
    this.screenShareZeroGrowthStreak = 0;
    // 共享结束后按摄像头当前意图恢复 camera 上行；用户中途关了摄像头则保持不推。
    await this.resumeCameraPublishAfterScreenShare(engine);
    infoScreenShare('stop_screen_share_done');
    // 诊断：stop 后 SDK 内部应已清掉 published screen track。若这里 has_screen_track_in_media_stream
    // 仍为 true 或 screen_pub_state 未归位，说明 stop 的清理没到位，是多次开关状态累积的嫌疑点。
    infoScreenShare('publish_state_after_stop', describeScreenPublishState(engine));
  }

  // 解析 rtcLocalVideoStats 中的屏幕共享上行项，只在状态跳变时打点：首次检测到 screen 上行
  // （uplink_detected）、连续未观察到新字节（uplink_no_bytes_observed，观察项非告警）、恢复增长
  // （uplink_bytes_resumed）。注意：发送侧统计只能反映本端编码/发送，无法证明对端收到画面；
  // 推流成功以 publish_state=Published 为权威依据。
  private handleScreenShareUplinkStats(
    rtcSdk: RtcSdkModule,
    stats: Array<{ track?: number; stats?: Record<string, unknown> }>,
  ): void {
    if (!this.isScreenShareActive) {
      return;
    }

    const screenTrackType = rtcSdk.AliRtcVideoTrack.AliRtcVideoTrackScreen;
    const screenStat = stats.find((item) => item.track === screenTrackType);
    if (!screenStat) {
      // 屏幕共享 active 却在上行统计里找不到 screen 项：推流未建立起来，只打一次以免刷屏。
      if (this.screenShareLastBytesSent !== null && !this.screenShareStalledLogged) {
        this.screenShareStalledLogged = true;
        warnScreenShare('uplink_missing', { publish_state: this.describeLastPublishState() });
      }
      return;
    }

    const bytesSent = Number(screenStat.stats?.bytesSent ?? 0);
    const framesEncoded = Number(screenStat.stats?.framesEncoded ?? 0);
    const framesPerSecond = Number(screenStat.stats?.framesPerSecond ?? 0);

    if (this.screenShareLastBytesSent === null) {
      infoScreenShare('uplink_detected', {
        bytes_sent: bytesSent,
        frames_encoded: framesEncoded,
        frames_per_second: framesPerSecond,
        publish_state: this.describeLastPublishState(),
        // SSRC 是对端订阅这路屏幕流的真正依据。跨多次开关对比：SSRC 每次都变、而对端仍订阅旧值，
        // 就是重协商/后端订阅没跟上的根因；SSRC 不变却收不到，则问题在后端订阅逻辑而非传输层。
        ssrc: screenStat.stats?.ssrc,
        rid: screenStat.stats?.rid,
        mid: screenStat.stats?.mid,
      });
      // 稳态 SDP：uplink_detected 约在 start 后 2s、重协商已 settle 时触发，这里再 dump 一次 SDP。
      // start 瞬间的 sdp_after_start 可能抓在协商中途（好那次那时甚至还没 screen m-line）；只有这份
      // 稳态快照能干净对比「好=sendonly」vs「坏=inactive」，避免被中途瞬时状态误导。
      const engine = this.engine;
      if (engine) {
        infoScreenShare('sdp_stable', describeScreenSdpMLine(engine));
      }
      this.screenShareLastBytesSent = bytesSent;
      return;
    }

    const isSending = bytesSent > this.screenShareLastBytesSent;
    this.screenShareLastBytesSent = bytesSent;

    if (!isSending) {
      // 观察项，不是结论：发送侧统计无法区分「画面静止（编码器只发极小 P 帧甚至零字节）」和
      // 「推流真卡死」。这里只如实记录「连续 N 次未观察到新字节」，供排查参考，不当告警。
      // 推流是否成功以 publish_state=Published 为权威依据；对端是否真收到需接收端 inbound 统计。
      this.screenShareZeroGrowthStreak += 1;
      if (this.screenShareZeroGrowthStreak >= SCREEN_SHARE_UPLINK_STALL_SAMPLES && !this.screenShareStalledLogged) {
        this.screenShareStalledLogged = true;
        infoScreenShare('uplink_no_bytes_observed', {
          bytes_sent: bytesSent,
          frames_per_second: framesPerSecond,
          publish_state: this.describeLastPublishState(),
          zero_growth_samples: this.screenShareZeroGrowthStreak,
          note: 'observation_only_not_stall_conclusion',
        });
      }
      return;
    }

    this.screenShareZeroGrowthStreak = 0;
    if (this.screenShareStalledLogged) {
      this.screenShareStalledLogged = false;
      infoScreenShare('uplink_bytes_resumed', { bytes_sent: bytesSent, frames_per_second: framesPerSecond });
    }

    // 正向心跳：仍在持续推送的证据，节流打印避免刷屏。
    this.screenShareStatsSampleCount += 1;
    if (this.screenShareStatsSampleCount % SCREEN_SHARE_UPLINK_HEARTBEAT_INTERVAL === 0) {
      infoScreenShare('uplink_heartbeat', {
        bytes_sent: bytesSent,
        frames_encoded: framesEncoded,
        frames_per_second: framesPerSecond,
        publish_state: this.describeLastPublishState(),
      });
    }
  }

  private describeLastPublishState(): string {
    return this.screenShareLastPublishState === null
      ? 'unknown'
      : describePublishState(this.screenShareLastPublishState);
  }

  private async probeScreenShareSupport(rtcSdk: RtcSdkModule): Promise<boolean> {
    const browserSupport = isBrowserScreenShareSupported();
    let sdkSupport = false;
    try {
      const supportResult = await rtcSdk.default.isSupported();
      sdkSupport = Boolean(supportResult?.detail?.isScreenShareSupported);
    } catch (err) {
      warnScreenShare('sdk_isSupported_error', {
        error_name: err instanceof Error ? err.name : typeof err,
        error_message: err instanceof Error ? err.message : String(err),
      });
    }
    const supported = browserSupport && sdkSupport;
    this.onScreenShareSupportedChange(supported);
    return supported;
  }

  private attachScreenShareTrackListener(track: MediaStreamTrack): void {
    this.detachScreenShareTrackListener();

    const listener = (): void => {
      this.handleScreenShareInterrupted().catch(() => {});
    };

    track.addEventListener('ended', listener, { once: true });
    this.screenShareTrack = track;
    this.screenShareTrackEndedListener = listener;
  }

  private detachScreenShareTrackListener(): void {
    if (this.screenShareTrack && this.screenShareTrackEndedListener) {
      this.screenShareTrack.removeEventListener('ended', this.screenShareTrackEndedListener);
    }
    this.screenShareTrack = null;
    this.screenShareTrackEndedListener = null;
  }

  private async handleScreenShareInterrupted(): Promise<void> {
    // track 'ended'（用户点浏览器 Stop、系统结束采集）或 display 设备异常都会走到这里。
    // 打点便于区分「用户主动停」与「共享中途被动断开导致对端没画面」。
    infoScreenShare('interrupted', { is_active: this.isScreenShareActive, has_engine: this.engine !== null });

    if (!this.isScreenShareActive) {
      return;
    }

    const engine = this.engine;
    if (!engine) {
      this.setScreenShareActive(false);
      return;
    }

    await this.stopScreenShare(engine);
  }

  private setScreenShareActive(active: boolean): void {
    if (this.isScreenShareActive === active) {
      return;
    }
    this.isScreenShareActive = active;
    this.onLocalScreenShareActiveChange(active);
  }

  private logConnectionStatus(status: LoggedRtcConnectionStatus, statusCode: number | string): void {
    const previous = this.lastLoggedConnectionStatus;
    if (previous?.status === status && previous.statusCode === statusCode) {
      return;
    }

    this.lastLoggedConnectionStatus = { status, statusCode };
    this.logger?.log('rtc', 'rtc_status_change', { status, status_code: statusCode });
  }

  private removeRemoteBinding(uid: string): void {
    this.remoteBindings = this.remoteBindings.filter((item) => item.uid !== uid);
    /*
     * 这个 uid 的画面已经撤了，缓存必须作废：SDK 侧的绑定随之失效，而同一个
     * uid 用同一个 videoTrack 再推回来是很常见的（远端摄像头关了又开）。
     * 不清就会被判成"没变"而跳过重绑，画面出不来。
     */
    if (this.appliedRemoteView?.uid === uid) {
      this.appliedRemoteView = null;
    }
    this.onRemoteBindingsChange([...this.remoteBindings]);
  }

  private upsertRemoteBinding(binding: RemoteVideoBinding): void {
    this.remoteBindings = this.remoteBindings.filter(
      (item) => item.uid !== binding.uid || item.videoTrack !== binding.videoTrack,
    );
    this.remoteBindings.push(binding);
    this.onRemoteBindingsChange([...this.remoteBindings]);
    this.applyRemoteView();
  }

  dispose(): Promise<void> {
    this.disposed = true;
    return this.stop();
  }
}
